import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { useToast } from '@chakra-ui/react';
import { ListingApiClient, DemotedListingApiClient } from 'api/listing';
import { ListingDataSuggestionApiClient } from 'api/listingDataSuggestion';
import { ListingModel, PaginationModel } from 'models';
import { JSONObject } from 'models/Api/types';
import { ListingStatus } from 'models/Listing/types';
import { useAuth } from 'contexts/AuthContext';

export const useListing = (id: string, options?: Omit<UseQueryOptions<ListingModel>, 'queryKey' | 'queryFn'>) => {
  return useQuery({
    queryKey: ['listings', id],
    queryFn: () => ListingApiClient.getListingById(id),
    enabled: !!id,
    ...options,
  });
};

export const useListings = (page = 1) => {
  return useQuery({
    queryKey: ['listings', { page }],
    queryFn: () => ListingApiClient.searchListings({ page }),
  });
};

export const useDemotedListing = (id: string, options?: Omit<UseQueryOptions<ListingModel>, 'queryKey' | 'queryFn'>) => {
  return useQuery<ListingModel>({
    queryKey: ['demoted-listing', id],
    queryFn: () => DemotedListingApiClient.getDemotedListingById(id),
    enabled: !!id,
    ...options,
  });
};

export const useDemotedListingSearch = (query: string, page: number = 1) => {
  return useQuery({
    queryKey: ['demoted-listings-search', query, page],
    queryFn: () => DemotedListingApiClient.searchDemotedListingsByQuery(query, page),
    enabled: !!query
  });
};

export const useListingSuggestionsForItem = (query: string, artist: string, page: number = 1, enabled: boolean = true) => {
  return useQuery({
    queryKey: ['listing-suggestions', query, artist, page],
    queryFn: () => ListingApiClient.searchListingSuggestionsForItem(query, artist, page),
    enabled: enabled && !!query && !!artist
  });
};

export const useArtistPendingListings = (
  artistId: string,
  page: number,
  options?: Omit<UseQueryOptions<PaginationModel<ListingModel>>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: ['artists', artistId, 'pendingListings', page],
    queryFn: () =>
      ListingApiClient.searchListings({
        page,
        artist_id: artistId,
        processing_status: [ListingStatus.PENDING],
        sort_by: 'date',
        sort_type: 'desc',
      }),
    enabled: !!artistId && page > 0,
    placeholderData: (previousData) => previousData,
    ...options,
  });
};

export const useMarkListingAsFake = () => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { currentUser } = useAuth();

  return useMutation({
    mutationFn: async (ids: string[]) => {
      const isBasicAdmin = currentUser?.isBasicAdmin();

      if (isBasicAdmin) {
        // For basic admins, create suggestions instead of directly modifying
        await Promise.all(
          ids.map(listingId =>
            ListingDataSuggestionApiClient.createListingDataSuggestion(listingId, { authentic: false })
          )
        );
      } else {
        // For full admins, directly mark as fake
        await ListingApiClient.markListingAsFake(ids);
      }
    },
    onSuccess: (_, ids) => {
      queryClient.invalidateQueries({ queryKey: ['listings'] });
      queryClient.invalidateQueries({ queryKey: ['listing-data-suggestions-detailed-status'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-pending-listings'] });

      const isBasicAdmin = currentUser?.isBasicAdmin();
      toast({
        title: isBasicAdmin
          ? `Suggestion${ids.length > 1 ? 's' : ''} to mark listing${ids.length > 1 ? 's' : ''} as fake created`
          : `Listing${ids.length > 1 ? 's' : ''} marked as fake`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to mark listing(s) as fake',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    },
  });
};

export const useMarkListingAsSet = () => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { currentUser } = useAuth();

  return useMutation({
    mutationFn: async (ids: string[]) => {
      const isBasicAdmin = currentUser?.isBasicAdmin();

      if (isBasicAdmin) {
        // For basic admins, create suggestions instead of directly modifying
        await Promise.all(
          ids.map(listingId =>
            ListingDataSuggestionApiClient.createListingDataSuggestion(listingId, { multiple_items: true })
          )
        );
      } else {
        // For full admins, directly mark as set
        await ListingApiClient.markListingAsSet(ids);
      }
    },
    onSuccess: (_, ids) => {
      queryClient.invalidateQueries({ queryKey: ['listings'] });
      queryClient.invalidateQueries({ queryKey: ['listing-data-suggestions-detailed-status'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-pending-listings'] });

      const isBasicAdmin = currentUser?.isBasicAdmin();
      toast({
        title: isBasicAdmin
          ? `Suggestion${ids.length > 1 ? 's' : ''} to mark listing${ids.length > 1 ? 's' : ''} as set created`
          : `Listing${ids.length > 1 ? 's' : ''} marked as set`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to mark listing(s) as set',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    },
  });
};

export const useMarkListingAsOriginal = () => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { currentUser } = useAuth();

  return useMutation({
    mutationFn: async (ids: string[]) => {
      const isBasicAdmin = currentUser?.isBasicAdmin();

      if (isBasicAdmin) {
        // For basic admins, create suggestions instead of directly modifying
        await Promise.all(
          ids.map(listingId =>
            ListingDataSuggestionApiClient.createListingDataSuggestion(listingId, { is_unique: true })
          )
        );
      } else {
        // For full admins, directly mark as original
        await ListingApiClient.markListingAsOriginal(ids);
      }
    },
    onSuccess: (_, ids) => {
      queryClient.invalidateQueries({ queryKey: ['listings'] });
      queryClient.invalidateQueries({ queryKey: ['listing-data-suggestions-detailed-status'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-pending-listings'] });

      const isBasicAdmin = currentUser?.isBasicAdmin();
      toast({
        title: isBasicAdmin
          ? `Suggestion${ids.length > 1 ? 's' : ''} to mark listing${ids.length > 1 ? 's' : ''} as original created`
          : `Listing${ids.length > 1 ? 's' : ''} marked as original`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to mark listing(s) as original',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    },
  });
};

export const useRejectListing = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (id: string) => ListingApiClient.rejectListing(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['listings'] });
      toast({
        title: 'Listing rejected',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to reject listing',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    },
  });
};

export const useUpdateListing = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: JSONObject }) => ListingApiClient.updateListing(id, data),
    onSuccess: (updatedListing) => {
      queryClient.invalidateQueries({ queryKey: ['listings'] });
      queryClient.setQueryData(['listings', updatedListing.id], updatedListing);
      toast({
        title: 'Listing updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to update listing',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    },
  });
};

export const useMigrateListingImage = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation<void, Error, { id: string; imageUrl: string }>({
    mutationFn: ({ id, imageUrl }) => ListingApiClient.migrateListingImage(id, imageUrl),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['listings', variables.id] });
      toast({
        title: 'Image migrated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to migrate image',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    },
  });
};

export const useMigrateDemotedListingImage = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation<void, Error, { id: string; imageUrl: string }>({
    mutationFn: ({ id, imageUrl }) => DemotedListingApiClient.migrateDemotedListingImage(id, imageUrl),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['demoted-listing', variables.id] });
      toast({
        title: 'Image migrated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to migrate image',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    },
  });
}; 
